/** @jsxImportSource theme-ui */
import PropTypes from "prop-types";
import { BodyText } from "./Text";
import { theme } from "./theme";

import { RAW_cssValue } from "../../nessie/stylingLib";
import { UnstyledButton } from "../..";

type TileGroupSelectOption = {
  title: string;
  value: string;
  imgSrc?: string;
};

type TileGroupSelectProps = {
  options: TileGroupSelectOption[];
  selectedValues: string[];
  setSelectedValues: (values: string[]) => void;
  imgSize?: number;
  itemsPerRow?: number;
  maxWidth?: number;
  cardHeight?: number;
};

export const TileGroupSelect = ({
  options,
  selectedValues,
  setSelectedValues,
  imgSize = 42,
  itemsPerRow = 3,
  maxWidth = 800,
  cardHeight = 138,
}: TileGroupSelectProps) => {
  const toggleValueSelection = (value: string) => {
    if (selectedValues.includes(value)) {
      const valuesWithRemovedSelection = selectedValues.filter((selectedValue) => selectedValue !== value);
      return setSelectedValues(valuesWithRemovedSelection);
    }

    setSelectedValues(selectedValues.concat(value));
  };

  return (
    <div sx={{ display: "flex", flexWrap: "wrap", maxWidth, justifyContent: "center", position: "relative" }}>
      {options.map((option) => {
        const isSelected = selectedValues.includes(option.value);

        return (
          <UnstyledButton
            role="checkbox"
            aria-checked={selectedValues.includes(option.value)}
            aria-label={option.title}
            tabIndex={0}
            key={option.value}
            onClick={() => toggleValueSelection(option.value)}
            sx={{
              padding: "dt_m",
              border: "2px solid",
              borderColor: isSelected ? "dt_aqua50" : "dt_taro30",
              borderRadius: RAW_cssValue("30px"),
              width: `${Math.round(100 / (itemsPerRow + 1))}%`,
              minWidth: maxWidth / (itemsPerRow + 1),
              height: cardHeight,
              alignItems: "center",
              margin: "dt_s",
              cursor: "pointer",
              backgroundColor: "dt_white",
              justifyContent: option.imgSrc ? "none" : "center",
              boxShadow: isSelected ? "none" : `0px 6px 0px ${theme.colors.quartz}`,
              display: "flex",
              flexDirection: "column",
              position: "relative",
              "&:hover": {
                boxShadow: "none",
              },
              transition: "box-shadow ease-in-out 0.2s",
            }}
          >
            {option.imgSrc ? (
              <div sx={{ paddingBottom: "dt_s", position: "relative" }}>
                <img height={imgSize || 42} width={imgSize || 42} src={option.imgSrc} alt={option.value} />
              </div>
            ) : null}
            <BodyText textAlign="center">{option.title}</BodyText>
          </UnstyledButton>
        );
      })}
    </div>
  );
};

const optionShape = PropTypes.shape({
  value: PropTypes.string.isRequired,
  imgSrc: PropTypes.string,
  title: PropTypes.string.isRequired,
});

TileGroupSelect.propTypes = {
  options: PropTypes.arrayOf(optionShape).isRequired,
  selectedValues: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedValues: PropTypes.func.isRequired,
  imgSize: PropTypes.number,
  itemsPerRow: PropTypes.number,
  maxWidth: PropTypes.number,
  cardHeight: PropTypes.number,
};
